// 多指标合并策略：UnionMergeStrategy 按 x 轴与维度键对齐合并多个指标表，输出合并后的数据与列
import { IMergeStrategy, ITableData, IDownloadContext } from '../types';
import { IMetricInfo } from '../../AIContent/hooks';

/**
 * 并集合并策略
 */
export class UnionMergeStrategy implements IMergeStrategy {
  merge(
    tableDataList: ITableData[],
    context: Partial<IDownloadContext>
  ): { data: any[]; columnsInfo: any[]; } | null {
    if (!tableDataList || tableDataList.length === 0) {
      return null;
    }

    // 只有一个组合时，直接返回原始 tableInfo
    if (tableDataList.length === 1) {
      return {
        data: tableDataList[0].data,
        columnsInfo: tableDataList[0].columnsInfo,
      };
    }

    const xAxis = context.xAxis || [];
    const firstXAxis = xAxis[0];
    const xAxisKey =
      typeof firstXAxis === 'object' && (firstXAxis as any)?.date_key
        ? (firstXAxis as any).date_key
        : typeof firstXAxis === 'string'
          ? firstXAxis
          : 'date';

    // 1. 拆分"维度列"和"指标列"
    const baseColumnsMap = new Map<string, any>(); // 维度列（时间 / 维度）
    const metricColumnsMap = new Map<string, any>(); // 指标列（data_key）

    const metricsInfo = context.metricsInfo || [];

    tableDataList.forEach(({ columnsInfo, metricKey }) => {
      columnsInfo.forEach((col: any) => {
        const key = col.origin_name || col.name;
        const isMetricCol =
          key === metricKey || metricsInfo.some((m) => m.data_key === key);

        if (isMetricCol) {
          if (!metricColumnsMap.has(key)) {
            metricColumnsMap.set(key, col);
          }
        } else {
          if (!baseColumnsMap.has(key)) {
            baseColumnsMap.set(key, col);
          }
        }
      });
    });

    // 2. 合并数据：按 x 轴 + 所有维度列 作为唯一键
    const dataMap = new Map<string, any>();
    const dimensionKeys = Array.from(baseColumnsMap.keys());

    tableDataList.forEach(({ data, metricKey }) => {
      data.forEach((row: any) => {
        const dimKeyParts: string[] = [];
        dimensionKeys.forEach((dimKey) => {
          const displayKey = `${dimKey}_display`;
          const value = row[displayKey] ?? row[dimKey];
          if (value !== undefined && value !== null) {
            dimKeyParts.push(`${dimKey}_${value}`);
          }
        });

        const rowKey = `${row[xAxisKey]}_${dimKeyParts.join('_')}`;

        if (!dataMap.has(rowKey)) {
          const newRow: any = {};
          // 填充所有维度列，优先使用 *_display 的展示值
          dimensionKeys.forEach((dimKey) => {
            const displayKey = `${dimKey}_display`;
            newRow[dimKey] = row[displayKey] ?? row[dimKey];
          });
          // 当前指标值写到原始字段名（data_key）上
          newRow[metricKey] = row[metricKey];
          dataMap.set(rowKey, newRow);
        } else {
          const existingRow = dataMap.get(rowKey);
          existingRow[metricKey] = row[metricKey];
        }
      });
    });

    // 3. 列顺序：维度列在前，指标列在后
    let mergedColumnsInfo: any[] = [
      ...Array.from(baseColumnsMap.values()),
      ...Array.from(metricColumnsMap.values()),
    ];

    // 4. 列显示名称对齐页面展示
    mergedColumnsInfo = this.mapColumnNames(
      mergedColumnsInfo,
      context.showMetrics as IMetricInfo[] || [],
      context.showFilters || []
    );

    return {
      data: Array.from(dataMap.values()),
      columnsInfo: mergedColumnsInfo,
    };
  }

  /**
   * 映射列显示名称
   */
  private mapColumnNames(
    columnsInfo: any[],
    showMetrics: IMetricInfo[],
    showFilters: Array<{ data_key?: string; name?: string; }>
  ): any[] {
    const metricNameMap = new Map<string, string>();
    showMetrics.forEach((metric) => {
      if (metric?.data_key) {
        metricNameMap.set(
          metric.data_key as string,
          metric.name || metric.data_key as string,
        );
      }
    });

    const filterNameMap = new Map<string, string>();
    showFilters.forEach((filter) => {
      if (filter?.data_key) {
        filterNameMap.set(
          filter.data_key,
          (filter as any).name || filter.data_key,
        );
      }
    });

    return columnsInfo.map((col: any) => {
      const key = col.origin_name || col.name;
      const nextCol = { ...col };

      if (metricNameMap.has(key)) {
        nextCol.name = metricNameMap.get(key);
      } else if (filterNameMap.has(key)) {
        nextCol.name = filterNameMap.get(key);
      }

      return nextCol;
    });
  }
}

